import { useCallback, useEffect, useMemo } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useV2PathListQuery } from "features/Path/hooks";
import { Path, PathId } from "features/Path/models";

/**
 * Hook for managing paths field array in template forms
 *
 * Provides consistent functionality across all template forms:
 * - Manages paths field array with react-hook-form
 * - Handles initial path from query param (newPathId)
 * - Tracks return path for navigation after creation
 * - Generates selected paths components with tags
 * - Provides selected path IDs for filtering
 *
 * @param form - React Hook Form instance
 * @returns Object containing paths field array utilities
 */
export function usePathsFieldArray<TFormData extends { paths: Array<{ id: string }> }>(
  form: UseFormReturn<TFormData>,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allPaths } = useV2PathListQuery("");

  const { fields: pathFields, append: appendPath, remove: removePath } = useFieldArray({
    control: form.control,
    name: "paths" as any,
    keyName: "fieldId",
  });

  // Set initial path by query param and manage query param cleanup
  useEffect(() => {
    const newPathId = searchParams.get("newPathId");
    const watchedPaths = form.watch("paths" as any);
    const isAlreadyEmpty = watchedPaths?.length === 0;

    if (newPathId) {
      if (isAlreadyEmpty) {
        appendPath({ id: newPathId } as any);
      }
      if (watchedPaths.map((p: any) => p.id).includes(newPathId)) {
        searchParams.delete("newPathId");
        setSearchParams(searchParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get return path ID from query param
  const returnPathId = useMemo<PathId | undefined>(() => {
    return searchParams.get("newPathId") || undefined;
  }, [searchParams]);

  // Add path callback
  const addPathId = useCallback((pathId: string) => appendPath({ id: pathId } as any), [appendPath]);

  // Selected path IDs
  const selectedPathIds = useMemo(
    () => (pathFields || []).map((field: any) => field.id),
    [pathFields],
  );

  // Get path data for a field
  const getPathData = useCallback(
    (fieldId: string) => {
      return allPaths?.find((p: Path) => p.id === fieldId);
    },
    [allPaths],
  );

  return {
    pathFields,
    appendPath,
    removePath,
    addPathId,
    selectedPathIds,
    getPathData,
    returnPathId,
    allPaths,
  };
}
